<template>
    <div id="page_space_before_prep_list" class="sbp sbp-list">
        <div class="sbp-main-layout">
            <header class="sbp-project-name-bar">
                <span class="sbp-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <h1 class="sbp-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
            </header>

            <div class="sbp-panel">
                <div class="sbp-panel-header">
                    <div class="sbp-panel-title">🧳 行前准备</div>
                    <div class="sbp-panel-actions">
                        <button type="button" class="sbp-btn sbp-btn-primary sbp-btn-sm" @click="openCreateDialog">
                            ＋ 新建行前准备
                        </button>
                    </div>
                </div>
                <div class="sbp-filter-bar">
                    <div class="sbp-search-box">
                        <i class="el-icon-search sbp-search-icon" />
                        <input v-model.trim="searchQuery" type="search" class="sbp-search-input"
                            placeholder="搜索名称、发布空间…" aria-label="搜索列表">
                    </div>
                    <select v-model="statusFilter" class="sbp-filter-select" aria-label="按状态筛选">
                        <option value="">全部状态</option>
                        <option value="1">已发布</option>
                        <option value="0">未发布</option>
                    </select>
                </div>
                <div class="sbp-panel-body">
                    <table v-show="rows.length" class="sbp-data-table">
                        <thead>
                            <tr>
                                <th>行前准备名称</th>
                                <th>模块数</th>
                                <th class="sbp-col-status">状态</th>
                                <th>发布空间</th>
                                <th>最近更新</th>
                                <th class="sbp-col-actions">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in rows" :key="row.id">
                                <td>
                                    <div class="sbp-cell-title">{{ row.title }}</div>
                                    <div class="sbp-cell-sub">{{ row.subtitle }}</div>
                                </td>
                                <td>{{ row.moduleCount }}</td>
                                <td class="sbp-col-status">
                                    <span v-if="row.status === 'published'" class="sbp-status-tag sbp-published">已发布</span>
                                    <span v-else-if="row.status === 'unpublished'" class="sbp-status-tag sbp-unpublished">未发布</span>
                                    <span v-else class="sbp-status-tag sbp-closed">已结束</span>
                                </td>
                                <td class="sbp-cell-publish-spaces">{{ row.publishSpacesText }}</td>
                                <td>{{ row.updatedAt }}</td>
                                <td class="sbp-col-actions">
                                    <span class="sbp-table-actions">
                                        <template v-if="row.status === 'published'">
                                            <button type="button" class="sbp-btn sbp-btn-primary sbp-btn-sm"
                                                @click="goEdit(row)">编辑</button>
                                            <button type="button" class="sbp-btn sbp-btn-unpublish sbp-btn-sm"
                                                @click="onUnpublish(row)">取消发布</button>
                                        </template>
                                        <template v-else-if="row.status === 'unpublished'">
                                            <button type="button" class="sbp-btn sbp-btn-default sbp-btn-sm"
                                                @click="goEdit(row)">编辑</button>
                                            <button type="button"
                                                class="sbp-btn sbp-btn-success sbp-btn-sm sbp-btn-publish-lit"
                                                @click="openPublishDialog(row)">发布</button>
                                        </template>
                                        <template v-else>
                                            <button type="button" class="sbp-btn sbp-btn-default sbp-btn-sm"
                                                @click="goView(row)">查看</button>
                                        </template>
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-show="!rows.length" class="sbp-table-empty">
                        <el-empty description="暂无行前准备，请点击「新建行前准备」创建" />
                    </div>
                </div>
                <div v-show="rows.length" class="sbp-pagination">
                    <span class="sbp-pagination-info">共 {{ pages.total }} 条</span>
                    <pagination :total="pages.total" :page.sync="pages.currentPage" :limit.sync="pages.pageSize"
                        :pageSizes="[10, 20, 30]" @pagination="handlePageChange">
                    </pagination>
                </div>
            </div>
        </div>

        <el-dialog title="新建行前准备" custom-class="sbp-create-dialog" :visible.sync="createDialogVisible" width="540px"
            append-to-body destroy-on-close @closed="resetCreateForm">
            <el-form class="sbp-create-form" label-width="138px" @submit.native.prevent>
                <el-form-item label="行前准备名称" required class="sbp-create-form-row">
                    <el-input v-model.trim="createName" maxlength="30" show-word-limit placeholder="请输入行前准备名称"
                        clearable />
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
                <el-button @click="createDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="createSaving" @click="submitCreate">保 存</el-button>
            </span>
        </el-dialog>

        <el-dialog title="选择发布的空间" :visible.sync="publishDialogVisible" width="520px" append-to-body
            custom-class="sbp-publish-dialog" @close="pendingPublishRow = null">
            <p class="sbp-space-picker-hint">请选择要将该行前准备发布到的结对空间（可多选）。学生仅在对应空间内可见该行前准备。</p>
            <el-checkbox-group v-model="selectedPublishSpaces" v-loading="publishSpacesLoading"
                class="sbp-space-picker-list">
                <div v-for="sp in publishSpaceOptions" :key="sp.spxId" class="sbp-space-picker-row"
                    :class="{ 'is-checked': selectedPublishSpaces.includes(sp.spxId) }">
                    <el-checkbox :label="sp.spxId" class="sbp-space-picker-cb" />
                    <div class="sbp-sp-body">
                        <span class="sbp-sp-name">{{ sp.value }}</span>
                        <div class="sbp-sp-meta">{{ sp.meta }}</div>
                    </div>
                </div>
            </el-checkbox-group>
            <el-empty v-if="!publishSpacesLoading && !publishSpaceOptions.length" description="暂无可发布的结对空间" />
            <span slot="footer" class="dialog-footer">
                <el-button @click="publishDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="publishSaving" @click="confirmPublish">确认发布</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import pagination from '@/common/community/pagination.vue';
    import {
        mapState
    } from 'vuex';

    function formatNow() {
        const d = new Date();
        const z = (n) => (n < 10 ? `0${n}` : `${n}`);
        return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())} ${z(d.getHours())}:${z(d.getMinutes())}`;
    }

    function isApiSuccess(res) {
        return res && (res.code === 0 || res.code === 200);
    }

    export default {
        name: 'SpaceBeforePrepList',
        components: {
            pagination,
        },
        data() {
            return {
                rows: [],
                pages: {
                    currentPage: 1,
                    pageSize: 10,
                    total: 0,
                },
                searchQuery: '',
                statusFilter: '',
                _searchDebounce: null,
                createDialogVisible: false,
                createName: '',
                createSaving: false,
                publishDialogVisible: false,
                pendingPublishRow: null,
                selectedPublishSpaces: [],
                publishSpaceOptions: [],
                publishSpacesLoading: false,
                publishSaving: false,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
        },
        watch: {
            searchQuery() {
                if (this._searchDebounce) {
                    clearTimeout(this._searchDebounce);
                }
                this._searchDebounce = setTimeout(() => {
                    this.pages.currentPage = 1;
                    this.fetchList();
                }, 400);
            },
            statusFilter() {
                this.pages.currentPage = 1;
                this.fetchList();
            },
            'currentProject.id'() {
                this.pages.currentPage = 1;
                this.fetchList();
            },
        },
        mounted() {
            this.fetchList();
        },
        beforeDestroy() {
            if (this._searchDebounce) {
                clearTimeout(this._searchDebounce);
            }
        },
        methods: {
            mapListItem(rec) {
                const st = Number(rec.status);
                const status = st === 1 ? 'published' : 'unpublished';
                const names = Array.isArray(rec.publishSpaceNames) ? rec.publishSpaceNames : [];
                const publishSpacesText = names.length ? names.join('、') : '—';
                const subtitle = names.length ? names.join('、') : (status === 'published' ? '—' : '待关联正式批次');
                return {
                    id: String(rec.id),
                    title: rec.name || '—',
                    subtitle,
                    moduleCount: rec.sectionCount != null ? rec.sectionCount : 0,
                    status,
                    publishSpacesText,
                    updatedAt: rec.updateTime || '—',
                    keywords: `${rec.name || ''} ${publishSpacesText}`,
                    defaultSub: subtitle,
                };
            },
            async fetchList() {
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.rows = [];
                    this.pages.total = 0;
                    return;
                }
                const params = {
                    projectId,
                    pageNum: this.pages.currentPage,
                    pageSize: this.pages.pageSize,
                };
                const kw = (this.searchQuery || '').trim();
                if (kw) {
                    params.keyword = kw;
                }
                if (this.statusFilter === '1' || this.statusFilter === '0') {
                    params.status = Number(this.statusFilter);
                }
                const res = await this.$Api.PrePrepare.page(params);
                if (isApiSuccess(res)) {
                    const data = res.data || {};
                    const records = data.records || [];
                    this.rows = records.map((r) => this.mapListItem(r));
                    this.pages.total = data.total != null ? data.total : 0;
                } else {
                    this.$message.error((res && res.msg));
                    this.rows = [];
                    this.pages.total = 0;
                }
            },
            handlePageChange({
                pageIndex,
                pageSize
            }) {
                this.pages.currentPage = pageIndex;
                this.pages.pageSize = pageSize;
                this.fetchList();
            },
            openCreateDialog() {
                this.createName = '';
                this.createDialogVisible = true;
            },
            resetCreateForm() {
                this.createName = '';
                this.createSaving = false;
            },
            async submitCreate() {
                const name = (this.createName || '').trim();
                if (!name) {
                    this.$message.warning('请输入行前准备名称');
                    return;
                }
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.$message.warning('未获取到当前项目');
                    return;
                }
                this.createSaving = true;
                try {
                    const res = await this.$Api.PrePrepare.create({
                        projectId,
                        name,
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success('创建成功');
                        this.createDialogVisible = false;
                        this.createName = '';
                        this.pages.currentPage = 1;
                        await this.fetchList();
                    } else {
                        this.$message.error((res && res.msg) || '创建失败');
                    }
                } catch (e) {
                    this.$message.error('创建失败');
                } finally {
                    this.createSaving = false;
                }
            },
            mapPublishSpaceItem(item) {
                const count = item.studentCount != null ? item.studentCount : 0;
                return {
                    spxId: item.spxId,
                    value: item.spxName || '—',
                    meta: `${count} 名学生`,
                };
            },
            async fetchPublishSpaces() {
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.publishSpaceOptions = [];
                    return;
                }
                this.publishSpacesLoading = true;
                try {
                    const res = await this.$Api.PrePrepare.publishSpaces({
                        projectId,
                    });
                    if (isApiSuccess(res)) {
                        const list = Array.isArray(res.data) ? res.data : [];
                        this.publishSpaceOptions = list.map((item) => this.mapPublishSpaceItem(item));
                    } else {
                        this.publishSpaceOptions = [];
                        this.$message.error((res && res.msg) || '获取发布空间失败');
                    }
                } catch (e) {
                    this.publishSpaceOptions = [];
                    this.$message.error('获取发布空间失败');
                } finally {
                    this.publishSpacesLoading = false;
                }
            },
            async openPublishDialog(row) {
                if (row.status !== 'unpublished') return;
                const target = this.rows.find((r) => r.id === row.id);
                if (!target) return;
                this.pendingPublishRow = target;
                this.selectedPublishSpaces = [];
                this.publishDialogVisible = true;
                await this.fetchPublishSpaces();
            },
            getSelectedPublishSpaceNames() {
                return this.selectedPublishSpaces
                    .map((id) => {
                        const sp = this.publishSpaceOptions.find((o) => o.spxId === id);
                        return sp ? sp.value : '';
                    })
                    .filter(Boolean);
            },
            async confirmPublish() {
                if (!this.pendingPublishRow) return;
                if (!this.selectedPublishSpaces.length) {
                    this.$message.warning('请至少选择一个要发布到的结对空间');
                    return;
                }
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.$message.warning('未获取到当前项目');
                    return;
                }
                const joined = this.getSelectedPublishSpaceNames().join('、');
                this.publishSaving = true;
                try {
                    const res = await this.$Api.PrePrepare.publish({
                        prepareId: Number(this.pendingPublishRow.id),
                        projectId,
                        spaceIds: this.selectedPublishSpaces.map((id) => Number(id)),
                    });
                    if (isApiSuccess(res)) {
                        this.$message.success(`已发布至：${joined}`);
                        this.publishDialogVisible = false;
                        this.pendingPublishRow = null;
                        await this.fetchList();
                    } else {
                        this.$message.error((res && res.msg) || '发布失败');
                    }
                } catch (e) {
                    this.$message.error('发布失败');
                } finally {
                    this.publishSaving = false;
                }
            },
            onUnpublish(row) {
                const target = this.rows.find((r) => r.id === row.id);
                if (!target || target.status !== 'published') return;
                const projectId = this.currentProject && this.currentProject.id;
                if (!projectId) {
                    this.$message.warning('未获取到当前项目');
                    return;
                }
                this.$confirm('取消发布后，学生在对应空间内将不再看到此行前准备。确定取消？', '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                }).then(async () => {
                    try {
                        const res = await this.$Api.PrePrepare.unpublish({
                            prepareId: Number(target.id),
                            projectId,
                        });
                        if (isApiSuccess(res)) {
                            this.$message.success('已取消发布，当前为未发布');
                            await this.fetchList();
                        } else {
                            this.$message.error((res && res.msg) || '取消发布失败');
                        }
                    } catch (e) {
                        this.$message.error('取消发布失败');
                    }
                }).catch(() => {});
            },
            goEdit(row) {
                this.$router.push({
                    path: '/home/spaceBeforePrep/edit',
                    query: {
                        id: row.id,
                        prepName: row.title,
                        mode: 'edit',
                    },
                });
            },
            goView(row) {
                this.$router.push({
                    path: '/home/spaceBeforePrep/edit',
                    query: {
                        id: row.id,
                        prepName: row.title,
                        mode: 'view',
                    },
                });
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409eff;
    @primary-dark: #337ecc;
    @primary-light: #ecf5ff;
    @success: #67c23a;
    @success-bg: #f0f9eb;
    @warning: #e6a23c;
    @warning-bg: #fdf6ec;
    @danger: #f56c6c;
    @info: #909399;
    @info-bg: #f4f4f5;
    @bg: #f5f7fa;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @text-placeholder: #c0c4cc;
    @border: #dcdfe6;
    @border-light: #ebeef5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .sbp {
        font-size: 14px;
        color: @text-primary;
        line-height: 1.5;
    }

    .sbp-main-layout {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .sbp-project-name-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .sbp-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .sbp-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
        flex: 1;
        min-width: 0;
    }

    .sbp-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .sbp-panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 10px;
    }

    .sbp-panel-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
    }

    .sbp-panel-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .sbp-filter-bar {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        background: @bg;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
    }

    .sbp-search-box {
        flex: 1;
        min-width: 200px;
        position: relative;
    }

    .sbp-search-input {
        width: 100%;
        padding: 7px 12px 7px 32px;
        border: 1px solid @border;
        border-radius: 6px;
        font-size: 13px;
        outline: none;
        transition: all 0.25s ease;
        box-sizing: border-box;

        &:focus {
            border-color: @primary;
            box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
        }
    }

    .sbp-search-icon {
        position: absolute;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
        color: @text-placeholder;
        font-size: 14px;
    }

    .sbp-filter-select {
        padding: 7px 12px;
        border: 1px solid @border;
        border-radius: 6px;
        font-size: 13px;
        outline: none;
        background: #fff;
        cursor: pointer;
    }

    .sbp-panel-body {
        padding: 0;
        overflow-x: auto;
        min-height: 200px;
    }

    .sbp-table-empty {
        padding: 48px 16px 64px;
        text-align: center;
    }

    .sbp-table-empty /deep/ .el-empty__description {
        margin-top: 12px;
    }

    .sbp-data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;

        thead th {
            text-align: center;
        }

        th {
            padding: 10px 12px;
            background: @bg;
            font-weight: 600;
            color: @text-regular;
            font-size: 12px;
            white-space: nowrap;
        }

        tbody td {
            padding: 12px;
            border-bottom: 1px solid @border-light;
            vertical-align: middle;
            text-align: center;
        }

        tbody tr:hover td {
            background: @primary-light;
        }
    }

    .sbp-col-actions {
        white-space: nowrap;
    }

    .sbp-cell-title {
        font-weight: 600;
        color: @text-primary;
    }

    .sbp-cell-sub {
        font-size: 12px;
        color: @text-secondary;
        margin-top: 2px;
    }

    .sbp-cell-publish-spaces {
        font-size: 13px;
        color: @text-regular;
        line-height: 1.45;
        max-width: 280px;
        word-break: break-word;
    }

    .sbp-col-status {
        white-space: nowrap;
        vertical-align: middle;
    }

    .sbp-status-tag {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        box-sizing: border-box;
        white-space: nowrap;
    }

    .sbp-published {
        background: @success-bg;
        color: @success;
    }

    .sbp-unpublished {
        background: @info-bg;
        color: @info;
    }

    .sbp-closed {
        background: @border-light;
        color: @text-secondary;
    }

    .sbp-table-actions {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-wrap: nowrap;
        white-space: nowrap;

        .sbp-btn {
            flex-shrink: 0;
        }

        .sbp-btn-publish-lit,
        .sbp-btn-unpublish {
            min-width: 72px;
            justify-content: center;
            box-sizing: border-box;
        }
    }

    .sbp-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 7px 14px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.25s ease;
        border: 1px solid transparent;
        font-family: inherit;
    }

    .sbp-btn-sm {
        padding: 5px 10px;
        font-size: 12px;
    }

    .sbp-btn-primary {
        background: @primary;
        color: #fff;
        border-color: @primary;

        &:hover {
            background: @primary-dark;
        }
    }

    .sbp-btn-default {
        background: #fff;
        color: @text-regular;
        border-color: @border;

        &:hover {
            color: @primary;
            border-color: @primary;
            background: @primary-light;
        }
    }

    .sbp-btn-success {
        background: @success;
        color: #fff;
        border-color: @success;

        &:hover {
            background: #529b2e;
            border-color: #529b2e;
        }
    }

    .sbp-btn-publish-lit {
        background: linear-gradient(180deg, #7fd063 0%, @success 100%);
        box-shadow: 0 0 0 1px rgba(103, 194, 58, 0.45), 0 2px 10px rgba(103, 194, 58, 0.35);
        font-weight: 600;

        &:hover {
            filter: brightness(1.02);
        }
    }

    .sbp-btn-unpublish {
        background: #fff;
        color: @warning;
        border-color: @warning;

        &:hover {
            background: @warning-bg;
            color: #b88230;
            border-color: #cf9236;
        }
    }

    .sbp-pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 14px 16px;
        border-top: 1px solid @border-light;

        .sbp-pagination-info {
            font-size: 13px;
            color: @text-secondary;
            line-height: 30px;
            white-space: nowrap;
        }

        /deep/ .pagination {
            margin: 0;
            text-align: right;
        }
    }

    .sbp-pagination-pages {
        display: flex;
        gap: 4px;
    }

    .sbp-page-btn {
        width: 30px;
        height: 30px;
        border: 1px solid @border;
        border-radius: 6px;
        background: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        color: @text-regular;
        transition: all 0.25s ease;
    }

    .sbp-page-btn-active {
        background: @primary;
        color: #fff;
        border-color: @primary;
    }

    .sbp-space-picker-hint {
        font-size: 13px;
        color: @text-secondary;
        margin: 0 0 14px;
        line-height: 1.5;
    }

    .sbp-space-picker-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 320px;
        overflow-y: auto;
    }

    .sbp-space-picker-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px 14px;
        border: 1px solid @border-light;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.25s ease;
        background: #fff;

        &:hover {
            border-color: @primary;
        }

        &.is-checked {
            border-color: @success;
            background: @success-bg;
        }
    }

    .sbp-space-picker-cb {
        margin-top: 2px;
        flex-shrink: 0;

        /deep/ .el-checkbox__label {
            display: none;
        }
    }

    .sbp-sp-body {
        flex: 1;
        min-width: 0;
    }

    .sbp-sp-name {
        font-weight: 600;
        font-size: 14px;
        color: @text-primary;
    }

    .sbp-sp-meta {
        font-size: 12px;
        color: @text-secondary;
        margin-top: 4px;
    }
</style>

<style lang="less">
    /* append-to-body 弹窗挂载在 body，需非 scoped */
    .sbp-create-dialog {
        .el-dialog__body {
            padding: 16px 24px 12px;
        }

        .sbp-create-form {
            .sbp-create-form-row.el-form-item {
                margin-bottom: 12px;
                display: flex;
                flex-wrap: nowrap;
                align-items: flex-start;

                &:before,
                &:after {
                    display: none;
                }
            }

            .sbp-create-form-row .el-form-item__label {
                flex: 0 0 138px;
                width: 138px !important;
                max-width: 138px;
                text-align: right;
                padding-right: 12px;
                line-height: 32px;
                white-space: nowrap;
                box-sizing: border-box;
                float: none;
            }

            .sbp-create-form-row .el-form-item__content {
                flex: 1;
                min-width: 0;
                margin-left: 0 !important;
                line-height: 32px;
            }

            .sbp-create-form-row .el-input {
                width: 100%;
            }
        }
    }
</style>
